// Bitcoin (native segwit, BIP-84) — derivation, validation, UTXO planning and
// signing. Browser-safe: pure @scure/noble stack, no WASM, no node crypto.
//
// Coin selection is DELIBERATELY delegated to @scure/btc-signer's selectUTXO:
// it computes the fee from the actual final vsize, creates the change output,
// and folds sub-dust (<546 sat) change into the fee — closing the three classic
// hand-rolled-BTC bugs (fee under-estimate, dust change, off-by-one sat) with
// one audited routine. planBtcTx (fee display) and buildAndSignBtcTx (real
// send) run the SAME selection, so the fee shown always equals the fee paid.

import { HDKey } from '@scure/bip32';
import { mnemonicToSeedSync } from '@scure/bip39';
import { hex } from '@scure/base';
import * as btc from '@scure/btc-signer';
import type { DerivedAddresses } from '../types';

/** BIP-84: m/84'/0'/0'/0/index → P2WPKH (bc1q…). */
const PATH = (index: number) => `m/84'/0'/0'/0/${index}`;

export const BTC_DUST_SAT = 546n;

/** bc1q address + compressed pubkey at `index` (mirrors deriveEvmAddresses). */
export function deriveBtcAddresses(seed: Uint8Array, index = 0): DerivedAddresses {
  const child = HDKey.fromMasterSeed(seed).derive(PATH(index));
  if (!child.publicKey) throw new Error('btc derivation failed: no public key');
  const pay = btc.p2wpkh(child.publicKey, btc.NETWORK);
  if (!pay.address) throw new Error('btc derivation failed: no address');
  return { eoa: pay.address, publicKey: hex.encode(child.publicKey) };
}

/** Private key bytes at send time — the seed never leaves the device. */
function btcPrivateKey(mnemonic: string, index = 0): Uint8Array {
  const child = HDKey.fromMasterSeed(mnemonicToSeedSync(mnemonic)).derive(PATH(index));
  if (!child.privateKey) throw new Error('btc derivation failed: no private key');
  return child.privateKey;
}

export function btcAddressFromMnemonic(mnemonic: string, index = 0): string {
  return deriveBtcAddresses(mnemonicToSeedSync(mnemonic), index).eoa;
}

/** Well-formed MAINNET address of any standard kind: P2PKH '1…', P2SH '3…',
 *  bech32 bc1q…, taproot bc1p… (recipients may be anything; our own receive
 *  addresses are always bc1q). Testnet (tb1/m/n/2) is rejected by NETWORK. */
export function isValidBtcAddress(address: string): boolean {
  try {
    btc.Address(btc.NETWORK).decode(address);
    return true;
  } catch {
    return false;
  }
}

export interface BtcUtxo {
  txid: string;
  vout: number;
  valueSat: bigint;
}

export interface BtcTxPlan {
  inputCount: number;
  feeSat: bigint;
  /** Whether a change output was created (sub-dust change folds into feeSat). */
  hasChange: boolean;
  /** Total sats leaving the wallet: amount + feeSat. */
  totalSat: bigint;
}

function toSelectInputs(utxos: BtcUtxo[], publicKey: Uint8Array) {
  const script = btc.p2wpkh(publicKey, btc.NETWORK).script;
  return utxos.map((u) => ({
    txid: hex.decode(u.txid),
    index: u.vout,
    witnessUtxo: { script, amount: u.valueSat },
  }));
}

function runSelection(p: {
  utxos: BtcUtxo[];
  to: string;
  amountSat: bigint;
  feeRateSatVb: number;
  changeAddress: string;
  publicKey: Uint8Array;
  createTx: boolean;
}) {
  const feePerByte = BigInt(Math.max(1, Math.ceil(p.feeRateSatVb)));
  const selected = btc.selectUTXO(
    toSelectInputs(p.utxos, p.publicKey),
    [{ address: p.to, amount: p.amountSat }],
    'default',
    {
      changeAddress: p.changeAddress,
      feePerByte,
      bip69: false,
      createTx: p.createTx,
      network: btc.NETWORK,
      dust: BTC_DUST_SAT,
      allowUnknownOutputs: false,
    },
  );
  if (!selected || selected.fee === undefined) throw new Error('insufficient_funds');
  return selected;
}

/**
 * Pure planner for the fee display — needs only the PUBLIC key (available in
 * wallet.addresses.publicKey), so quotes work without unlocking the seed.
 * Throws Error('insufficient_funds') when the UTXOs can't cover amount + fee.
 */
export function planBtcTx(p: {
  utxos: BtcUtxo[];
  to: string;
  amountSat: bigint;
  feeRateSatVb: number;
  changeAddress: string;
  publicKeyHex: string;
}): BtcTxPlan {
  const selected = runSelection({ ...p, publicKey: hex.decode(p.publicKeyHex), createTx: false });
  return {
    inputCount: selected.inputs.length,
    feeSat: selected.fee!,
    hasChange: selected.change,
    totalSat: p.amountSat + selected.fee!,
  };
}

/**
 * Largest amount (sats) that can be sent at `feeRateSatVb` with the given UTXOs.
 * Uses the same selectUTXO path as the real send, so Max never overquotes.
 * `to` only affects output script size — pass a P2WPKH (e.g. the change address)
 * when the final recipient is unknown yet (swap payin).
 */
export function planBtcMaxSend(p: {
  utxos: BtcUtxo[];
  to: string;
  feeRateSatVb: number;
  changeAddress: string;
  publicKeyHex: string;
}): { maxSat: bigint; feeSat: bigint } {
  const total = p.utxos.reduce((s, u) => s + u.valueSat, 0n);
  if (total === 0n || p.utxos.length === 0) return { maxSat: 0n, feeSat: 0n };

  let lo = 0n;
  let hi = total;
  let best = 0n;
  let bestFee = 0n;
  while (lo <= hi) {
    const mid = (lo + hi) / 2n;
    if (mid <= 0n) {
      lo = 1n;
      continue;
    }
    try {
      const plan = planBtcTx({ ...p, amountSat: mid });
      best = mid;
      bestFee = plan.feeSat;
      lo = mid + 1n;
    } catch {
      hi = mid - 1n;
    }
  }
  return { maxSat: best, feeSat: bestFee };
}

export interface SignedBtcTx {
  hex: string;
  txid: string;
  feeSat: bigint;
  vsize: number;
}

/** Build + sign a P2WPKH spend. Same selection as planBtcTx (createTx: true),
 *  then sign every input and finalize. Default sequence (no RBF — no fee-bump
 *  UI exists, keep the tx non-replaceable and simple). */
export function buildAndSignBtcTx(p: {
  utxos: BtcUtxo[];
  to: string;
  amountSat: bigint;
  feeRateSatVb: number;
  changeAddress: string;
  mnemonic: string;
  index?: number;
}): SignedBtcTx {
  const priv = btcPrivateKey(p.mnemonic, p.index ?? 0);
  const child = HDKey.fromMasterSeed(mnemonicToSeedSync(p.mnemonic)).derive(PATH(p.index ?? 0));
  const selected = runSelection({
    utxos: p.utxos,
    to: p.to,
    amountSat: p.amountSat,
    feeRateSatVb: p.feeRateSatVb,
    changeAddress: p.changeAddress,
    publicKey: child.publicKey!,
    createTx: true,
  });
  const tx = selected.tx;
  if (!tx) throw new Error('btc tx build failed');
  tx.sign(priv);
  tx.finalize();
  return {
    hex: tx.hex,
    txid: tx.id,
    feeSat: selected.fee!,
    vsize: tx.vsize,
  };
}
